import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Center, Group, Loader, Modal, NumberInput, Select, SimpleGrid, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type SchedTask, scheduleApi, undoPaths } from '../../api';
import { TASK_STATUS, type TaskStatusKey, UNIT_DAYS, formatDate } from '../../format';
import { guardedStep, useUndo } from '../../ui/undo';
import { inverseTaskBody, nextTaskCode } from './taskEdit';

/** What the dialog is for: a new task in a project (chosen here unless given), or an existing one. */
export type TaskEditTarget =
  | { mode: 'create'; projectId: string | null }
  | { mode: 'edit'; projectId: string; taskId: string };

interface Draft {
  code: string;
  name: string;
  wbsNodeId: string | null;
  durationDays: number | string;
  startFixed: string;
  finish: string;
  status: TaskStatusKey;
  owner: string;
  position: string;
  deadline: string;
  deliverable: string;
  note: string;
  doneOn: string;
}

const EMPTY: Draft = { code: '', name: '', wbsNodeId: null, durationDays: 1, startFixed: '', finish: '', status: 'open', owner: '', position: '', deadline: '', deliverable: '', note: '', doneOn: '' };

function draftOf(t: SchedTask): Draft {
  return {
    code: t.code, name: t.name, wbsNodeId: t.wbsNodeId, durationDays: t.durationDays,
    startFixed: t.constraintType === 'startNoEarlierThan' ? (t.constraintDate ?? '') : '',
    finish: t.earlyFinish ?? '', status: t.status, owner: t.owner ?? '', position: t.position ?? '',
    deadline: t.deadline ?? '', deliverable: t.deliverable ?? '', note: t.note ?? '', doneOn: t.doneOn ?? '',
  };
}

const orNull = (s: string): string | null => (s.trim() ? s.trim() : null);

/**
 * One dialog to create or edit a task from the task list (and anywhere a
 * full form beats clicking into cells). Writes through the schedule's own
 * routes; a change can be undone like one in the plan (ADR 0008).
 */
export function TaskEditModal({ target, projects, onClose }: {
  target: TaskEditTarget | null;
  /** Projects to choose from when creating without a fixed project. */
  projects: readonly { projectId: string; label: string }[];
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal opened={target !== null} onClose={onClose} size="lg" title={target?.mode === 'edit' ? 'Vorgang bearbeiten' : 'Vorgang anlegen'}>
      {target && <Body key={target.mode === 'edit' ? target.taskId : 'new'} target={target} projects={projects} onClose={onClose} />}
    </Modal>
  );
}

function Body({ target, projects, onClose }: { target: TaskEditTarget; projects: readonly { projectId: string; label: string }[]; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const undo = useUndo();
  const [projectId, setProjectId] = useState<string | null>(target.projectId);
  const schedule = useQuery({ queryKey: ['schedule', projectId], queryFn: () => scheduleApi.schedule(projectId!), enabled: !!projectId });
  const wbs = useQuery({ queryKey: ['wbs', projectId], queryFn: () => scheduleApi.wbs(projectId!), enabled: !!projectId });
  const task = target.mode === 'edit' ? schedule.data?.tasks.find((t) => t.id === target.taskId) : undefined;
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const set = (p: Partial<Draft>): void => setDraft((d) => ({ ...d, ...p }));

  // Fill the form once the data is there: the task's values, or a free number for a new one.
  useEffect(() => {
    if (!schedule.data || !projectId) return;
    const key = target.mode === 'edit' ? target.taskId : projectId;
    if (loadedFor === key) return;
    if (target.mode === 'edit') {
      if (!task) return;
      setDraft(draftOf(task));
    } else {
      setDraft((d) => ({ ...d, code: nextTaskCode(schedule.data.tasks.map((t) => t.code)) }));
    }
    setLoadedFor(key);
  }, [schedule.data, projectId, target, task, loadedFor]);

  const refresh = async (p: string): Promise<void> => {
    await Promise.all([['agenda'], ['schedule', p], ['schedule-overview'], ['plan', p]].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!projectId) throw new Error('Bitte ein Projekt wählen.');
      const duration = Number(draft.durationDays);
      if (target.mode === 'create') {
        const created = await scheduleApi.createTask(projectId, {
          code: draft.code.trim(), name: draft.name.trim(), wbsNodeId: draft.wbsNodeId, durationDays: duration, status: draft.status,
          ...(draft.status === 'done' && draft.doneOn ? { doneOn: draft.doneOn } : {}),
          owner: orNull(draft.owner), position: orNull(draft.position), deadline: orNull(draft.deadline), deliverable: orNull(draft.deliverable), note: orNull(draft.note),
        });
        if (draft.startFixed) await scheduleApi.updateTask(projectId, created.id, { startDate: draft.startFixed });
        return;
      }
      if (!task) throw new Error('Den Vorgang gibt es nicht mehr.');
      const before = draftOf(task);
      const body: Record<string, unknown> = {};
      if (draft.code.trim() !== before.code) body.code = draft.code.trim();
      if (draft.name.trim() !== before.name) body.name = draft.name.trim();
      if (draft.wbsNodeId !== before.wbsNodeId) body.wbsNodeId = draft.wbsNodeId;
      if (draft.status !== before.status) body.status = draft.status;
      if (draft.status === 'done' && draft.doneOn && draft.doneOn !== before.doneOn) body.doneOn = draft.doneOn;
      for (const k of ['owner', 'position', 'deadline', 'deliverable', 'note'] as const) {
        if (orNull(draft[k]) !== orNull(before[k])) body[k] = orNull(draft[k]);
      }
      if (draft.startFixed !== before.startFixed) body.startDate = draft.startFixed || null;
      // An end moved by hand wins over the duration, as in the plan; otherwise the duration counts.
      if (draft.finish && draft.finish !== before.finish && duration !== 0) body.finishDate = draft.finish;
      else if (duration !== Number(before.durationDays)) body.durationDays = duration;
      if (Object.keys(body).length === 0) return;
      const back = inverseTaskBody(task, body);
      const saved = await scheduleApi.updateTask(projectId, task.id, body);
      undo.record(guardedStep(`Vorgang ${task.code}`, 'PATCH', undoPaths.task(projectId, task.id), back, saved as unknown as Record<string, unknown>));
    },
    onSuccess: onClose,
    onSettled: () => (projectId ? refresh(projectId) : undefined),
  });

  const duration = Number(draft.durationDays);
  const valid = !!projectId && draft.code.trim() !== '' && draft.name.trim() !== '' && Number.isInteger(duration) && duration >= 0;
  const loading = !!projectId && (schedule.isPending || (target.mode === 'edit' && loadedFor === null));
  if (target.mode === 'edit' && schedule.data && !task) return <Alert color="red">Diesen Vorgang gibt es nicht mehr.</Alert>;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
      <Stack gap="sm">
        {save.isError && <Alert color="red">{save.error instanceof ApiError || save.error instanceof Error ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        {target.mode === 'create' && target.projectId === null && (
          <Select label="Projekt" required searchable placeholder="Projekt wählen" data={projects.map((p) => ({ value: p.projectId, label: p.label }))} value={projectId} onChange={(v) => { setProjectId(v); setLoadedFor(null); set({ wbsNodeId: null }); }} />
        )}
        {loading ? <Center h={120}><Loader size="sm" /></Center> : projectId && (
          <>
            <Group grow align="flex-start">
              <TextInput label="Nummer" description="im Projekt eindeutig" required value={draft.code} onChange={(e) => set({ code: e.currentTarget.value })} maw={160} />
              <TextInput label="Bezeichnung" required data-autofocus value={draft.name} onChange={(e) => set({ name: e.currentTarget.value })} />
            </Group>
            <Select label="Phase" placeholder="keine" clearable data={(wbs.data ?? []).map((n) => ({ value: n.id, label: `${n.code}  ${n.name}` }))} value={draft.wbsNodeId} onChange={(v) => set({ wbsNodeId: v })} />
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <NumberInput label={`Dauer (${UNIT_DAYS})`} description="0 = Meilenstein" min={0} allowDecimal={false} value={draft.durationDays} onChange={(v) => set({ durationDays: v, finish: '' })} />
              <TextInput type="date" label="Anfang frühestens" description="leer = aus den Vorgängern" value={draft.startFixed} onChange={(e) => set({ startFixed: e.currentTarget.value })} />
              {target.mode === 'edit' && duration !== 0 && (
                <TextInput type="date" label="Ende" description={task?.earlyFinish ? `gerechnet ${formatDate(task.earlyFinish)}; ändert die Dauer` : 'ändert die Dauer'} value={draft.finish} onChange={(e) => set({ finish: e.currentTarget.value })} />
              )}
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <Stack gap={4}>
                <Select label="Status" allowDeselect={false} data={(Object.keys(TASK_STATUS) as TaskStatusKey[]).map((k) => ({ value: k, label: TASK_STATUS[k].label }))} value={draft.status} onChange={(v) => v && set({ status: v as TaskStatusKey })} />
                {draft.status === 'done' && (
                  <TextInput size="xs" type="date" label="erledigt am" description="zählt beim Fortschreiben" value={draft.doneOn} onChange={(e) => set({ doneOn: e.currentTarget.value })} />
                )}
              </Stack>
              <TextInput label="Verantwortlich" placeholder="z. B. A oder A/TWP" value={draft.owner} onChange={(e) => set({ owner: e.currentTarget.value })} />
              <TextInput type="date" label="Spätestens" description="verschiebt nichts, warnt nur" value={draft.deadline} onChange={(e) => set({ deadline: e.currentTarget.value })} />
            </SimpleGrid>
            <Group grow align="flex-start">
              <TextInput label="Position" placeholder="z. B. Pos. 1" value={draft.position} onChange={(e) => set({ position: e.currentTarget.value })} maw={160} />
              <TextInput label="Ergebnis" placeholder="was vorliegt, wenn er erledigt ist" value={draft.deliverable} onChange={(e) => set({ deliverable: e.currentTarget.value })} />
            </Group>
            <Textarea label="Notiz" autosize minRows={2} maxRows={6} value={draft.note} onChange={(e) => set({ note: e.currentTarget.value })} />
            {target.mode === 'create' && <Text size="xs" c="dimmed">Anordnungsbeziehungen legt man im Terminplan an; ohne sie beginnt der Vorgang am Projektstart oder am Tag „Anfang frühestens“.</Text>}
          </>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" disabled={!valid || loading} loading={save.isPending}>{target.mode === 'edit' ? 'Speichern' : 'Anlegen'}</Button>
        </Group>
      </Stack>
    </form>
  );
}
